import React, { useState } from "react";
import { Btn, Eyebrow } from "../../components/Primitives";
import { api } from "../../lib/api";
import logger from "../../lib/logger";

const FEELINGS = [
    { v: "great", label: "Great — want to see them again" },
    { v: "good", label: "Good — open to another" },
    { v: "neutral", label: "Neutral — not sure" },
    { v: "not_a_fit", label: "Not a fit" },
];

const WHAT_HAPPENED = [
    { v: "rescheduled", label: "We rescheduled" },
    { v: "cancelled_mutual", label: "We both cancelled" },
    { v: "cancelled_other", label: "They cancelled on me" },
    { v: "cancelled_me", label: "I cancelled" },
    { v: "no_show", label: "They didn't show up" },
];

export default function Checkin({ user, dateId, onDone, onBack }) {
    const [met, setMet] = useState(null);
    const [feeling, setFeeling] = useState(null);
    const [whatHappened, setWhatHappened] = useState(null);
    const [comment, setComment] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const canSubmit =
        (met === true && feeling) ||
        (met === false && whatHappened);

    const submit = async () => {
        setSubmitting(true);
        try {
            await api.dating.submitCheckin(dateId, {
                user_id: user.session_id,
                met: met === true,
                feeling: met === true ? feeling : null,
                what_happened: met === false ? whatHappened : null,
                comment: comment || null,
            });
            onDone();
        } catch (e) {
            logger.error("checkin failed", e);
            alert(e?.response?.data?.detail || "Could not submit check-in.");
        }
        setSubmitting(false);
    };

    return (
        <div className="flex-1 flex flex-col bg-cream" data-testid="checkin-screen">
            <div className="px-6 pt-6 pb-2 flex items-center justify-between">
                <button data-testid="checkin-back-btn" onClick={onBack} className="tap-target text-[11px] tracking-[0.28em] uppercase text-ink-muted">← Back</button>
                <span className="text-[10px] tracking-[0.32em] uppercase text-ink-muted">After your date</span>
            </div>

            <div className="px-7 pt-4 pb-2">
                <Eyebrow>How did it go</Eyebrow>
                <h1 className="mt-2 font-display text-3xl text-burgundy leading-[1.15]">
                    Quick <span className="italic">check-in.</span>
                </h1>
                <p className="mt-2 text-sm text-ink-muted leading-snug">
                    Two minutes. We use this to improve future matches and flag no-shows.
                </p>
            </div>

            <div className="flex-1 px-7 py-5 overflow-y-auto space-y-6">
                <section>
                    <div className="text-[10px] tracking-[0.32em] uppercase text-ink-muted mb-2">Did you meet?</div>
                    <div className="grid grid-cols-2 gap-2">
                        <button
                            data-testid="met-yes"
                            onClick={() => setMet(true)}
                            className={`py-3 text-xs tracking-[0.18em] uppercase border ${met === true ? "bg-burgundy text-cream border-burgundy" : "border-burgundy/15 text-burgundy"}`}
                        >Yes, we met</button>
                        <button
                            data-testid="met-no"
                            onClick={() => setMet(false)}
                            className={`py-3 text-xs tracking-[0.18em] uppercase border ${met === false ? "bg-burgundy text-cream border-burgundy" : "border-burgundy/15 text-burgundy"}`}
                        >No, we didn't</button>
                    </div>
                </section>

                {met === true && (
                    <section>
                        <div className="text-[10px] tracking-[0.32em] uppercase text-ink-muted mb-2">How was it?</div>
                        <div className="space-y-2">
                            {FEELINGS.map(f => (
                                <button
                                    key={f.v}
                                    data-testid={`feeling-${f.v}`}
                                    onClick={() => setFeeling(f.v)}
                                    className={`w-full text-left px-4 py-3 text-sm border ${feeling === f.v ? "bg-burgundy text-cream border-burgundy" : "border-burgundy/15 text-ink hover:border-burgundy/40"}`}
                                >{f.label}</button>
                            ))}
                        </div>
                    </section>
                )}

                {met === false && (
                    <section>
                        <div className="text-[10px] tracking-[0.32em] uppercase text-ink-muted mb-2">What happened?</div>
                        <div className="space-y-2">
                            {WHAT_HAPPENED.map(w => (
                                <button
                                    key={w.v}
                                    data-testid={`what-${w.v}`}
                                    onClick={() => setWhatHappened(w.v)}
                                    className={`w-full text-left px-4 py-3 text-sm border ${whatHappened === w.v ? "bg-burgundy text-cream border-burgundy" : "border-burgundy/15 text-ink hover:border-burgundy/40"}`}
                                >{w.label}</button>
                            ))}
                        </div>
                    </section>
                )}

                {met !== null && (
                    <section>
                        <div className="text-[10px] tracking-[0.32em] uppercase text-ink-muted mb-2">Anything else? (optional)</div>
                        <textarea
                            data-testid="checkin-comment"
                            value={comment}
                            onChange={e => setComment(e.target.value)}
                            rows={3}
                            maxLength={600}
                            placeholder="Optional notes — just for us."
                            className="w-full bg-white border border-burgundy/15 p-3 text-sm text-ink focus:outline-none focus:border-burgundy/60"
                        />
                    </section>
                )}
            </div>

            <div className="px-6 pb-8 pt-3 border-t border-burgundy/10 bg-cream">
                <Btn data-testid="checkin-submit-btn" variant="primary" onClick={submit} disabled={!canSubmit || submitting}>
                    {submitting ? "Sending…" : "Submit check-in"}
                </Btn>
            </div>
        </div>
    );
}
